import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchBills, fetchBill, attachPaymentProof, type AttachPaymentProofInput } from './api';
import type { BillsListFilters } from './types';

export const billsKeys = {
  all: ['bills'] as const,
  list: (filters: BillsListFilters) => [...billsKeys.all, 'list', filters] as const,
  detail: (id: string) => [...billsKeys.all, 'detail', id] as const,
};

export function useBills(filters: BillsListFilters) {
  return useQuery({
    queryKey: billsKeys.list(filters),
    queryFn: () => fetchBills(filters),
    staleTime: 60_000,
    retry: false,
  });
}

export function useBill(id: string | null) {
  return useQuery({
    queryKey: id ? billsKeys.detail(id) : ['bills', 'detail', 'none'],
    queryFn: () => fetchBill(id as string),
    enabled: !!id,
    staleTime: 60_000,
    retry: false,
  });
}

export function useAttachPaymentProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ billId, input }: { billId: string; input: AttachPaymentProofInput }) =>
      attachPaymentProof(billId, input),
    onSuccess: (_, { billId }) => {
      qc.invalidateQueries({ queryKey: billsKeys.all });
      qc.invalidateQueries({ queryKey: billsKeys.detail(billId) });
      toast.success('Payment proof attached');
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Failed to attach proof';
      toast.error(msg);
    },
  });
}
